import { Badge, Icon, toast } from "@/shared/components";
import { usePulsingBoxShadow } from "@/shared/hooks";
import { copyToClipboard } from "@/shared/utils";
import { motion, useReducedMotion } from "motion/react";
import { PropsWithChildren } from "react";
import { Card } from "./ui";

type OngewensteGebeurtenisCardProps = {
    spoorwebNummer: string;
    omschrijving: string;
    trajectOfBaanvak: string;
    onClick: () => void;
    animated?: boolean;
    status: string;
    volgnummer: number;
};

export function OngewensteGebeurtenisCard({
    spoorwebNummer,
    omschrijving,
    trajectOfBaanvak,
    onClick,
    status,
    volgnummer,
    animated = false,
}: Readonly<OngewensteGebeurtenisCardProps>) {
    const paddedSpoorwebNummer = spoorwebNummer.padStart(7, "0");

    const copySpoorwebnummer = () => {
        void copyToClipboard(spoorwebNummer);
        return toast("Spoorwebnummer gekopieerd");
    };

    return (
        <Card
            variant="spoorweb"
            asChild={animated}
            element="li"
            data-testid={`ongewenste-gebeurtenis-card-${paddedSpoorwebNummer}`}
            data-name="spoorweb-card"
        >
            <MaybeAnimated animated={animated}>
                <div className="flex items-start justify-between">
                    <span className="hidden" data-testid="status">
                        {status}
                    </span>
                    <span className="hidden" data-testid="volgnummer">
                        {volgnummer}
                    </span>
                    <div className="flex items-center gap-1.5">
                        <span
                            data-testid="spoorwebnummer"
                            className="text-content-default leading-m max-w-[10ch] truncate text-xs font-medium"
                        >
                            #{paddedSpoorwebNummer}
                        </span>
                        <button
                            data-testid="copy-spoorwebnummer"
                            onClick={copySpoorwebnummer}
                            title="Kopieer spoorwebnummer"
                            aria-label="Kopieer spoorwebnummer"
                        >
                            <Icon name="copy" size="xs" />
                        </button>
                    </div>
                    <Badge variant="pink-subtle">Spoorweb</Badge>
                </div>
                <button
                    onClick={onClick}
                    className="flex w-full items-center justify-between"
                    aria-label="Maak dossier van ongewenste-gebeurtenis"
                    data-testid="create-dossier"
                >
                    <div className="flex flex-col items-start gap-1.5 truncate">
                        <div data-testid="traject-of-baanvak" className="text-s text-content-emphasis leading-none font-bold">
                            {trajectOfBaanvak}
                        </div>
                        <p data-testid="omschrijving" title={omschrijving} className="text-s text-content-default leading-s truncate">
                            {omschrijving}
                        </p>
                    </div>
                    <Icon name="chevron-right" strokeWidth="thick" />
                </button>
            </MaybeAnimated>
        </Card>
    );
}

const MaybeAnimated = ({
    children,
    className,
    animated,
    ...props
}: PropsWithChildren<{
    animated: boolean;
    className?: string;
}>) => {
    const prefersReducedMotion = useReducedMotion();
    const { controls, initial } = usePulsingBoxShadow("var(--outline-color-accent-pink-default)");

    if (animated && !prefersReducedMotion) {
        return (
            <motion.li data-name="maybe-animated" layout animate={controls} initial={initial} className={className} {...props}>
                {children}
            </motion.li>
        );
    }

    // if not animated, Card is already rendering a `li`
    return (
        <div className={className} data-name="maybe-animated" {...props}>
            {children}
        </div>
    );
};
